import { Box } from '#/components/Box/Box';
import { useAppConfig } from '#/config/AppConfigContext';
import { ClientOnly } from '#/containers/ClientOnly';
import { useUser } from '#/contexts/UserEnvironmentContext';
import type { CmsApiPagePost } from '#/modules/cms/api/types/CmsApiPage';
import { CmsAuthorCard } from '#/modules/cms/components/AuthorCard/CmsAuthorCard';
import {
  CmsBodyHolder,
  CmsBodyHolderWidth,
} from '#/modules/cms/components/BodyHolder/CmsBodyHolder';
import { CmsTagList } from '#/modules/cms/components/TagList/CmsTagList';
import { getCmsApiPageHomePageMeta } from '#/modules/cms/helpers/getCmsApiPageHomePageMeta';
import { clientOnlyLazy } from '#/utils/clientOnlyLazy';

type Props = {
  content: CmsApiPagePost;
};

const LazyOpenWebWidget = clientOnlyLazy(() =>
  import('#/modules/cms/components/Comments/OpenWebWidget').then((module) => ({
    default: module.OpenWebWidget,
  })),
);

const LazyCmsReactions = clientOnlyLazy(() =>
  import('#/modules/cms/components/Reactions/CmsReactions').then((module) => ({
    default: module.CmsReactions,
  })),
);

export function CmsPostPageLayoutBelowBlocks({ content }: Props) {
  const { user } = useUser();
  const { homePageUrl } = getCmsApiPageHomePageMeta({ content });

  const { appConfig } = useAppConfig();
  const openwebEnabled = Boolean(appConfig.openwebSpotId);

  return (
    <>
      {content.tags.length > 0 && (
        <Box mb={32}>
          <CmsBodyHolder $width={CmsBodyHolderWidth.NARROW_LAYOUT}>
            <CmsTagList
              homePageUrl={content.subsite.homePage?.url}
              tags={content.tags}
            />
          </CmsBodyHolder>
        </Box>
      )}

      {content.author && (
        <CmsBodyHolder $width={CmsBodyHolderWidth.NARROW_LAYOUT}>
          <CmsAuthorCard
            author={content.author}
            homePageUrl={homePageUrl}
            postedDate={content.postedDate}
            showDate={content.showDate}
          />
        </CmsBodyHolder>
      )}

      {content.hasReactions || content.hasSocialSharing ? (
        <CmsBodyHolder $width={CmsBodyHolderWidth.NARROW_LAYOUT}>
          <ClientOnly>
            <LazyCmsReactions content={content} user={user} />
          </ClientOnly>
        </CmsBodyHolder>
      ) : null}

      {content.enableComments && openwebEnabled && (
        <CmsBodyHolder $width={CmsBodyHolderWidth.NARROW_LAYOUT}>
          <ClientOnly>
            <LazyOpenWebWidget
              postId={content.id}
              relativePath={content.url}
              userId={user ? user.id : undefined}
              hasIdealistProfileFeature={
                user ? Boolean(user.hasIdealistProfileFeature) : false
              }
              hasPublicProfile={user ? user.hasPublishedIdealistProfile : false}
            />
          </ClientOnly>
        </CmsBodyHolder>
      )}
    </>
  );
}
